import React, { Suspense, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { useRovoAgentCreateAnalytics } from '@atlaskit/rovo-agent-analytics/create';
import { Subscriber, usePublish } from '@atlaskit/rovo-triggers/main';
import type {
	SolutionArchitectAgentActivationFlowStartedPayload,
	SolutionArchitectAgentActivationFlowStoppedPayload,
} from '@atlaskit/rovo-triggers/solution-architect-types';
import type { Payload } from '@atlaskit/rovo-triggers/types';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { Build } from '../../../../types';
import { ActionButton } from '../../../card-footer/action-button';
import { useSolutionPlan } from '../../../solution-plan-context';
import { useHandoffAction } from '../../../utils/use-handoff-action';
import { messages as commonMessages } from '../../messages';

import type { footerActions_AtlassianAtlassianStudioQuery } from './__generated__/footerActions_AtlassianAtlassianStudioQuery.graphql';
import { messages } from './messages';

type BuildAgentProps = {
	build: Build;
	canManageAgents: boolean;
};

type AgentFooterActionsProps = { build: Build };

const BuildAgentButton = ({ build, canManageAgents }: BuildAgentProps) => {
	di(useHandoffAction);

	const { formatMessage } = useIntl();
	const { isSubmitting, startHandoff } = useHandoffAction(build, {
		handoffPayloadDataFactory: (solutionBuildResponse) => ({
			...(fg('rovo_agent_versioning_enabled') && {
				ari: solutionBuildResponse.ari,
			}),
			buildId: solutionBuildResponse.buildId!,
			type: 'ROVO_AGENT',
			conversationId: build.conversationId,
		}),
	});
	const { sendAnalyticsEvent } = useAnalytics();
	const [, { trackCreateSession }] = useRovoAgentCreateAnalytics({
		touchPoint: 'solution-architect-plan-card',
	});
	const additionalAnalyticsAttributesEnabled = fg('additional-analytics-params-for-sa');

	const handleClick = () => {
		startHandoff();
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'agentSolutionPlanDraft',
			attributes: {
				buildType: build.type,
				buildNumber: build.number,
				buildState: build.state,
				solutionId: build.solutionId,
				solutionVersion: build.solutionVersion,
				conversationId: build.conversationId,
				...(additionalAnalyticsAttributesEnabled && { draftBuildId: build.id }),
			},
		});
		trackCreateSession('saDraft', {
			draftBuildId: build.id,
		});
	};

	const renderButton = () => (
		<ActionButton
			build={build}
			appearance="primary"
			onClick={handleClick}
			isLoading={isSubmitting}
			isDisabled={!canManageAgents}
		>
			{formatMessage(messages.buildAgentButtonLabel)}
		</ActionButton>
	);

	return (
		<Tooltip
			content={formatMessage(commonMessages.permissionDeniedTooltip)}
			canAppear={() => !canManageAgents}
		>
			{renderButton()}
		</Tooltip>
	);
};

const isActivationLifecycleEvent = (
	payload: Payload,
): payload is
	| SolutionArchitectAgentActivationFlowStartedPayload
	| SolutionArchitectAgentActivationFlowStoppedPayload =>
	[
		'solution-architect-agent-activation-flow-started',
		'solution-architect-agent-activation-flow-stopped',
	].includes(payload.type);

const ActivateAgentButton = ({ build, canManageAgents }: BuildAgentProps) => {
	di(useHandoffAction, usePublish);

	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const [activationInProgress, setActivationInProgress] = useState(false);

	const handleSubscriberEvent = (payload: Payload) => {
		if (!isActivationLifecycleEvent(payload) || payload.data.draftBuildId !== build.id) {
			return;
		}

		switch (payload.type) {
			case 'solution-architect-agent-activation-flow-started':
				setActivationInProgress(true);
				break;
			case 'solution-architect-agent-activation-flow-stopped':
				setActivationInProgress(false);
				break;
		}
	};

	/**
	 * When activating an agent from the plan card, we do the initial handoff call
	 * first to ensure redirecting to the draft agent page. And then we publish a
	 * solution-architect-agent-activation event to notify the agent studio to activate
	 * the agent.
	 *
	 * This is likely to change in the future, when we support draft agent and can
	 * activate the agent directly from the plan card.
	 */
	const { sendAnalyticsEvent } = useAnalytics();
	const [, { trackCreateSession }] = useRovoAgentCreateAnalytics({
		touchPoint: 'solution-architect-plan-card',
	});

	const { startHandoff } = useHandoffAction(build, {
		handoffPayloadDataFactory: (solutionBuildResponse) => ({
			buildId: solutionBuildResponse.buildId!,
			...(fg('rovo_agent_versioning_enabled') && {
				ari: solutionBuildResponse.ari,
			}),
			type: 'ROVO_AGENT',
			conversationId: build.conversationId,
			...(fg('fe_enable_sa_externally') && {
				shouldActivateAgent: true,
			}),
		}),
		...(!fg('fe_enable_sa_externally') && {
			onSuccessCallback: (solutionBuildResponse) => {
				publish({
					type: 'solution-architect-agent-activation',
					source: 'ai-mate',
					data: {
						draftBuildId: solutionBuildResponse.buildId!,
						...(fg('rovo_agent_versioning_enabled') && {
							ari: solutionBuildResponse.ari,
						}),
					},
				});
			},
		}),
	});
	const additionalAnalyticsAttributesEnabled = fg('additional-analytics-params-for-sa');

	const handleClick = () => {
		publish({
			type: 'solution-architect-agent-activation-flow-started',
			source: 'ai-mate',
			data: {
				draftBuildId: build.id,
			},
		});
		startHandoff();
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'activated',
			actionSubject: 'solutionPlan',
			actionSubjectId: 'agentSolutionPlan',
			...(additionalAnalyticsAttributesEnabled && { draftBuildId: build.id }),
		});
		trackCreateSession('createFlowActivate', {
			draftBuildId: build.id,
		});
	};

	const renderButton = () => (
		<ActionButton
			build={build}
			appearance="primary"
			onClick={handleClick}
			isLoading={activationInProgress}
			isDisabled={!canManageAgents}
		>
			{formatMessage(
				build.ari && fg('rovo_agent_versioning_enabled')
					? messages.publishAgentButtonLabel
					: messages.activateAgentButtonLabel,
			)}
		</ActionButton>
	);

	return (
		<>
			<Subscriber topic="ai-mate" onEvent={handleSubscriberEvent} />
			<Tooltip
				content={formatMessage(commonMessages.permissionDeniedTooltip)}
				canAppear={() => !canManageAgents}
			>
				{renderButton()}
			</Tooltip>
		</>
	);
};

// LoadingButton is used as a fallback for the Suspense component while the permissions query is loading.
const LoadingButton = ({ build }: AgentFooterActionsProps) => {
	const { formatMessage } = useIntl();
	const message =
		build.state === 'WAITING' ? messages.buildAgentButtonLabel : messages.activateAgentButtonLabel;
	return (
		<ActionButton build={build} appearance="primary" isLoading>
			{formatMessage(message)}
		</ActionButton>
	);
};

const AgentFooterActionsWithQuery = ({ build }: AgentFooterActionsProps): React.JSX.Element => {
	di();

	const { cloudId } = useSolutionPlan();

	const data = useLazyLoadQuery<footerActions_AtlassianAtlassianStudioQuery>(
		graphql`
			query footerActions_AtlassianAtlassianStudioQuery($cloudId: ID!, $skip: Boolean!) {
				atlassianStudio_userSiteContext(cloudId: $cloudId) @skip(if: $skip) {
					... on AtlassianStudioUserSiteContextOutput {
						userPermissions {
							isAbleToCreateAgents
						}
					}
				}
			}
		`,
		{ cloudId: cloudId || '', skip: !cloudId },
	);

	const canManageAgents =
		!!data.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents;

	return build.state === 'WAITING' ? (
		<BuildAgentButton build={build} canManageAgents={canManageAgents} />
	) : (
		<ActivateAgentButton build={build} canManageAgents={canManageAgents} />
	);
};

export const AgentFooterActions = (props: AgentFooterActionsProps): React.JSX.Element => {
	return (
		<Suspense fallback={<LoadingButton {...props} />}>
			<AgentFooterActionsWithQuery {...props} />
		</Suspense>
	);
};
